// Ciudades del directorio con página pSEO propia.
// Derivado del dato REAL de prod tras el saneamiento de `city` (2026-08-14):
// solo ciudades con ≥8 listings activos (evita thin content). Ver
// plan de saneamiento pSEO. Para añadir una ciudad, confirmar volumen ≥8 en
// public.directory_listings y añadir la entrada aquí (slug en minúscula sin acentos).

export interface DirectoryCity {
  /** Segmento de URL: /directorio/{slug} */
  slug: string;
  /** Valor EXACTO del campo `city` en BD — se envía al endpoint /directory?city= */
  display: string;
  /** Centro del mapa de la landing de ciudad */
  center: { latitude: number; longitude: number; zoom: number };
}

export const DIRECTORY_CITIES: DirectoryCity[] = [
  { slug: 'madrid', display: 'Madrid', center: { latitude: 40.4168, longitude: -3.7038, zoom: 12 } },
  { slug: 'malaga', display: 'Málaga', center: { latitude: 36.7213, longitude: -4.4214, zoom: 12 } },
  { slug: 'sevilla', display: 'Sevilla', center: { latitude: 37.3891, longitude: -5.9845, zoom: 12 } },
  { slug: 'cordoba', display: 'Córdoba', center: { latitude: 37.8882, longitude: -4.7794, zoom: 13 } },
  { slug: 'palma', display: 'Palma', center: { latitude: 39.5696, longitude: 2.6502, zoom: 13 } },
  { slug: 'albacete', display: 'Albacete', center: { latitude: 38.9943, longitude: -1.8585, zoom: 13 } },
  { slug: 'las-palmas-de-gran-canaria', display: 'Las Palmas de Gran Canaria', center: { latitude: 28.1235, longitude: -15.4363, zoom: 12 } },
  { slug: 'murcia', display: 'Murcia', center: { latitude: 37.9922, longitude: -1.1307, zoom: 13 } },
  { slug: 'almeria', display: 'Almería', center: { latitude: 36.8340, longitude: -2.4637, zoom: 13 } },
];

/** slug (minúscula) → ciudad. */
export const DIRECTORY_CITY_BY_SLUG: Record<string, DirectoryCity> = Object.fromEntries(
  DIRECTORY_CITIES.map((c) => [c.slug, c]),
);

/** Quita acentos y normaliza a minúsculas: "Córdoba" → "cordoba".
 *  Decodifica primero por si el segmento llega URL-encoded (Next puede pasar "c%C3%B3rdoba"). */
export function stripAccents(input: string): string {
  let s = input;
  try {
    s = decodeURIComponent(input);
  } catch {
    // input ya decodificado o con % inválido — usar tal cual
  }
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/** display normalizado (sin acentos ni espacios/guiones) → ciudad. */
const CITY_BY_NORMALIZED: Record<string, DirectoryCity> = Object.fromEntries(
  DIRECTORY_CITIES.flatMap((c) => {
    const keys = new Set([
      c.slug,
      stripAccents(c.slug).replace(/[\s-]+/g, ''),
      stripAccents(c.display).replace(/[\s-]+/g, ''),
    ]);
    return [...keys].map((k) => [k, c] as const);
  }),
);

/**
 * Resuelve un segmento de URL o nombre de ciudad a una DirectoryCity, tolerante a acentos,
 * espacios y guiones. "cordoba", "Córdoba", "las-palmas-de-gran-canaria",
 * "Las Palmas de Gran Canaria" → la ciudad correcta (slug canónico sin acento).
 */
export function resolveDirectoryCity(segment: string): DirectoryCity | undefined {
  const raw = stripAccents(segment);
  return DIRECTORY_CITY_BY_SLUG[raw] || CITY_BY_NORMALIZED[raw.replace(/[\s-]+/g, '')];
}

/** ¿Este segmento de URL es una ciudad del directorio (no una ficha)? Tolerante a acentos. */
export function isDirectoryCitySlug(slug: string): boolean {
  return resolveDirectoryCity(slug) !== undefined;
}

/**
 * Redirects (308) de la variante acentuada de cada ciudad → su slug canónico sin acento.
 * Se consume en next.config.ts para que /directorio/córdoba → /directorio/cordoba a nivel de
 * servidor (infalible, no depende del render on-demand). Solo genera entrada si el display
 * acentuado difiere del slug canónico.
 */
export function directoryCityAccentRedirects(): { source: string; destination: string; permanent: boolean }[] {
  const seen = new Set<string>();
  const out: { source: string; destination: string; permanent: boolean }[] = [];
  for (const c of DIRECTORY_CITIES) {
    const accented = c.display.toLowerCase().replace(/\s+/g, '-'); // "córdoba", "las palmas..." → "las-palmas..."
    if (accented !== c.slug && !seen.has(accented)) {
      seen.add(accented);
      out.push({ source: `/directorio/${accented}`, destination: `/directorio/${c.slug}`, permanent: true });
    }
  }
  return out;
}
